import styled, { useTheme } from 'styled-components';
import Modal, { FullHeightModalContent } from '../../../components/Modal';
import { BodyMedium, Heading4Text } from '../../../components/Texts';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { selectThemeMode } from '../../../redux/slices/themeSlice';
import Steps from '../../../components/Steps';
import { useGetGiftsQuery } from '../../../redux/api/authorized/gifts';
import { selectPrivyToken } from '../../../redux/slices/auth';
import {
  useCompleteOnboardingMutation,
  useGetCurrentUserProfileQuery,
} from '../../../redux/api/authorized/auth';
import { useNavigate } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import {
  BREAKPOINT_GIFT_CLAIMED_CREATE_GIFT_CLICKED,
  BREAKPOINT_GIFT_CLAIMED_MAYBE_LATER_CLICKED,
  BREAKPOINT_GIFT_CLAIMED_VIEWED,
  sendEvent,
} from '../../../common/analytics';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  justify-content: center;
  align-items: center;
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: auto;
    flex-direction: column;
  }
`;

const Image = styled.img`
  width: 100%;
  max-width: 500px;
  height: auto;
  max-height: 60vh;
  object-fit: contain;
  object-position: center;
`;

const GiftClaimed = ({ onClose, fromDashboard = false }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const selectedTheme = useSelector(selectThemeMode);
  const [currentStep, setCurrentStep] = useState(0);
  const [completeOnboarding] = useCompleteOnboardingMutation();
  const navigate = useNavigate();
  const { refetch: refetchCurrentUser } = useGetCurrentUserProfileQuery();
  const token = useSelector(selectPrivyToken);
  const breakpointRaffleEnabled = useFlag('enableBreakpointRaffle');

  const { data: gifts, refetch: refetchGifts } = useGetGiftsQuery({
    skip: !token,
  });

  useEffect(() => {
    refetchGifts();
  }, [refetchGifts]);

  const tradeImage = `/onboarding/trade-${isMobile ? 'mobile' : 'desktop'}${isMobile ? `-${selectedTheme}` : ''}.webp`;
  const giftClaimedImage = `/onboarding/gift-claimed-${isMobile ? 'mobile' : 'desktop'}-${selectedTheme}.webp`;
  const giftImage = `/onboarding/gift-${isMobile ? 'mobile' : 'desktop'}-${selectedTheme}.webp`;
  const breakpointRaffleImage = `/breakpoint/raffle-${isMobile ? 'mobile' : 'desktop'}.webp`;

  const imageMapper = fromDashboard
    ? {
        0: tradeImage,
        1: giftImage,
      }
    : breakpointRaffleEnabled
      ? {
          0: giftClaimedImage,
          1: tradeImage,
          2: giftImage,
          3: breakpointRaffleImage,
        }
      : {
          0: giftClaimedImage,
          1: tradeImage,
          2: giftImage,
        };

  const giftClaimedTitle = 'Gift Claimed';
  const tradeTitle = 'Trade Effortlessly';
  const giftTitle = 'Gift Easily';
  const breakpointRaffleTitle = 'Gift to participate';

  const titleMapper = fromDashboard
    ? {
        0: tradeTitle,
        1: giftTitle,
      }
    : breakpointRaffleEnabled
      ? {
          0: giftClaimedTitle,
          1: tradeTitle,
          2: giftTitle,
          3: breakpointRaffleTitle,
        }
      : {
          0: giftClaimedTitle,
          1: tradeTitle,
          2: giftTitle,
        };

  const giftClaimedDescription =
    'Your tokens are safely stored in your Portfolio.';
  const tradeDescription = 'Discover new investment opportunities.';
  const giftDescription = 'Share crypto with friends quickly.';
  const breakpointRaffleDescription =
    'Each gift sent = one entry to win $1,000.';

  const descriptionMapper = fromDashboard
    ? {
        0: tradeDescription,
        1: giftDescription,
      }
    : breakpointRaffleEnabled
      ? {
          0: giftClaimedDescription,
          1: tradeDescription,
          2: giftDescription,
          3: breakpointRaffleDescription,
        }
      : {
          0: giftClaimedDescription,
          1: tradeDescription,
          2: giftDescription,
        };

  useEffect(() => {
    if (breakpointRaffleEnabled && currentStep === 3) {
      sendEvent(BREAKPOINT_GIFT_CLAIMED_VIEWED);
    }
  }, [breakpointRaffleEnabled, currentStep]);

  return (
    <Modal onClose={onClose}>
      <FullHeightModalContent>
        <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
          <Heading4Text
            style={{
              fontWeight: '600',
              textAlign: 'center',
              color: theme.text.title,
            }}
          >
            {titleMapper[currentStep]}
          </Heading4Text>
          <BodyMedium
            style={{
              color: theme.text.regular,
              textAlign: 'center',
            }}
          >
            {descriptionMapper[currentStep]}
          </BodyMedium>
        </div>
        <Image src={imageMapper[currentStep]} />
        <ButtonContainer>
          {breakpointRaffleEnabled && currentStep === 3 && !fromDashboard && (
            <Button
              fullWidth={isMobile}
              loading={loading}
              variant='white'
              onClick={async () => {
                setLoading(true);
                await completeOnboarding().unwrap();
                await refetchCurrentUser();
                sendEvent(BREAKPOINT_GIFT_CLAIMED_MAYBE_LATER_CLICKED);
                setLoading(false);
                onClose();
                navigate('/app/home');
              }}
            >
              Maybe later
            </Button>
          )}
          <Button
            fullWidth={isMobile}
            loading={loading}
            onClick={async () => {
              if (
                currentStep ===
                  (fromDashboard ? 1 : breakpointRaffleEnabled ? 3 : 2) ||
                (!fromDashboard && gifts?.receivedGifts?.length > 1)
              ) {
                setLoading(true);
                await completeOnboarding().unwrap();
                await refetchCurrentUser();
                setLoading(false);
                onClose();
                if (!fromDashboard && breakpointRaffleEnabled) {
                  navigate('/app/gifts/create');
                  sendEvent(BREAKPOINT_GIFT_CLAIMED_CREATE_GIFT_CLICKED);
                } else {
                  navigate('/app/home');
                }
              } else {
                setCurrentStep(currentStep + 1);
              }
            }}
          >
            CONTINUE
          </Button>
        </ButtonContainer>
        {(fromDashboard || gifts?.receivedGifts?.length <= 1) && (
          <Steps
            steps={fromDashboard ? 2 : breakpointRaffleEnabled ? 4 : 3}
            currentStep={currentStep}
          />
        )}
      </FullHeightModalContent>
    </Modal>
  );
};

export default GiftClaimed;
